import PageNotAuthorized from "@src/backpack-console/PageNotAuthorized";
import { useFeatureFlag } from "@src/backpack-console/featureFlags";
import {
  ComponentProps,
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useMemo,
} from "react";
import {
  AccessLevel,
  RoleBasedPermissionsFeature,
  useAllPermissionsClaimsAccessLevels,
} from "./permissionsClaims";

type FeatureAccessChecks = {
  featureAccessChecks: Map<
    RoleBasedPermissionsFeature,
    AccessLevel | undefined
  >;
};

type PreFeatureAccessContextType = FeatureAccessChecks & {
  guardedFeature: RoleBasedPermissionsFeature | null;
  guardedFeatureAccessLevel: AccessLevel | undefined;
};
type GuardedFeatureAccessContextType = FeatureAccessChecks & {
  guardedFeature: RoleBasedPermissionsFeature;
  guardedFeatureAccessLevel: AccessLevel;
};
type NonGuardedFeatureAccessContextType = FeatureAccessChecks & {
  guardedFeature: null;
  guardedFeatureAccessLevel: undefined;
};
export type FeatureAccessContextType =
  | GuardedFeatureAccessContextType
  | NonGuardedFeatureAccessContextType;

const defaultValue = {
  featureAccessChecks: new Map<
    RoleBasedPermissionsFeature,
    AccessLevel | undefined
  >(),
  guardedFeature: null,
  guardedFeatureAccessLevel: undefined,
} satisfies FeatureAccessContextType;

export const FeatureAccessContext =
  createContext<FeatureAccessContextType>(defaultValue);

/**
 * Stores which feature(s) have been evaluated and the user's access level.
 * `undefined` means that the user does not have access
 * Any queries or mutations in child components should check this context via
 * the `usePreRequestFeatureAccessCheck` hook to see if the feature has been checked
 * for the feature/access needed for the route and log an error if the
 * feature hasn't been checked or if the access isn't enough.
 */
export const FeatureAccessContextProvider: FunctionComponent<
  PropsWithChildren<
    {
      readonly pageNotAuthorizedVariant?: ComponentProps<
        typeof PageNotAuthorized
      >["variant"];
      readonly pageNotAuthorizedPageName?: ComponentProps<
        typeof PageNotAuthorized
      >["pageName"];
      /** `null` renders nothing, for gating an element that has no denial state (e.g. a nav item). */
      readonly PageNotAuthorizedElement?: JSX.Element | null;
    } & (
      | {
          readonly features?: RoleBasedPermissionsFeature[];
          readonly guardedFeature: RoleBasedPermissionsFeature;
        }
      | {
          readonly guardedFeature?: never;
          readonly features: RoleBasedPermissionsFeature[];
        }
    )
  >
> = ({
  children,
  pageNotAuthorizedVariant,
  pageNotAuthorizedPageName,
  PageNotAuthorizedElement,
  guardedFeature,
  features: _features,
}) => {
  const allAccessLevels = useAllPermissionsClaimsAccessLevels();

  const featureAccessChecks = useMemo(() => {
    const allAccessLevelsMap = Object.fromEntries(
      allAccessLevels.map(({ feature, access }) => [feature, access] as const)
    ) satisfies Partial<Record<RoleBasedPermissionsFeature, AccessLevel>>;

    const features = [
      ...(guardedFeature ? [guardedFeature] : []),
      ...(_features ?? []),
    ];

    return new Map(
      features.map((feature) => [feature, allAccessLevelsMap[feature]] as const)
    );
  }, [_features, allAccessLevels, guardedFeature]);

  const guardedFeatureAccessLevel = guardedFeature
    ? featureAccessChecks.get(guardedFeature)
    : undefined;

  const contextValue = useMemo(
    () => ({
      guardedFeatureAccessLevel,
      guardedFeature: guardedFeature ?? null,
      featureAccessChecks,
    }),
    [featureAccessChecks, guardedFeature, guardedFeatureAccessLevel]
  );

  if (hasGuardedFeatureAccess(contextValue) || hasFeatureAccess(contextValue)) {
    return (
      <FeatureAccessContext.Provider value={contextValue}>
        {children}
      </FeatureAccessContext.Provider>
    );
  }

  if (typeof PageNotAuthorizedElement === "undefined") {
    return (
      <PageNotAuthorized
        pageName={pageNotAuthorizedPageName ?? guardedFeature}
        reason="permissions"
        variant={pageNotAuthorizedVariant}
      />
    );
  }
  return PageNotAuthorizedElement;
};

function hasGuardedFeatureAccess(
  contextValue: PreFeatureAccessContextType
): contextValue is GuardedFeatureAccessContextType {
  return (
    !!contextValue.guardedFeature && !!contextValue.guardedFeatureAccessLevel
  );
}

function hasFeatureAccess(
  contextValue: PreFeatureAccessContextType
): contextValue is NonGuardedFeatureAccessContextType {
  return (
    !contextValue.guardedFeature &&
    [...contextValue.featureAccessChecks.values()].some(Boolean)
  );
}

export function useFeatureAccessChecks() {
  const checked = useContext(FeatureAccessContext).featureAccessChecks;
  return checked;
}

/**
 * Returns undefined for feature(s) not checked in context or if hook is used outside context provider, does not throw
 */
export function useFeatureAccess(
  features: RoleBasedPermissionsFeature[]
): { feature: RoleBasedPermissionsFeature; access: AccessLevel | undefined }[];
export function useFeatureAccess(
  feature: RoleBasedPermissionsFeature
): AccessLevel | undefined;
export function useFeatureAccess(
  arg: RoleBasedPermissionsFeature | RoleBasedPermissionsFeature[]
) {
  const isRoleBasedPermissionsFlagEnabled =
    useFeatureFlag("role-based-permissions", "off") === "v1";

  const featureAccessChecks =
    useContext(FeatureAccessContext).featureAccessChecks;

  return useMemo(() => {
    if (Array.isArray(arg)) {
      return arg.map((feature) => ({
        feature,
        access: isRoleBasedPermissionsFlagEnabled
          ? featureAccessChecks.get(feature)
          : "edit",
      }));
    }

    return isRoleBasedPermissionsFlagEnabled
      ? featureAccessChecks.get(arg)
      : "edit";
  }, [arg, featureAccessChecks, isRoleBasedPermissionsFlagEnabled]);
}

/**
 *
 * @param feature The feature that has already been checked in context and is guaranteed to be
 * one of `edit | read`. Throws an error if the feature checked in context doesn't match the argument
 * or one wasn't checked which means this hook cannot be used
 */
export const useGuardedFeatureAccess = (
  feature: RoleBasedPermissionsFeature
) => {
  const { guardedFeature, guardedFeatureAccessLevel } =
    useContext(FeatureAccessContext);

  const isRoleBasedPermissionsFlagEnabled =
    useFeatureFlag("role-based-permissions", "off") === "v1";

  if (!isRoleBasedPermissionsFlagEnabled) return "edit";

  if (guardedFeature !== feature) {
    throw new Error(
      `Guarded feature mismatch in hook argument (${feature}) and FeatureAccessContext (${guardedFeature})`
    );
  }

  return guardedFeatureAccessLevel;
};
